import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  Box,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SmartPasswordField from "../../../../commonComponents/SmartPasswordField";
import { debounce } from "lodash";
import { api } from "../../../../redux/api";
import { useDispatch, useSelector } from "react-redux";
import { ExpandMore, MoreHorizOutlined } from "@mui/icons-material";
import { SectionHeader } from "../../Forms/SectionHeader";
import { decryptString, encryptString } from "../../../../utils/utilFunctions";
import WorkflowSessionModal from "../../WorkflowSessionModal";

function WorkflowEncryptDecrypt({ connectionDetails, screen, updateAndSaveData, disableEncrypt, selectedNode }) {
  const { t } = useTranslation();
  const { account } = useSelector((state) => state.queryParams);
  const dispatch = useDispatch();
  const [encryption, setEncryption] = useState(false);
  const [removePlainText, setRemovePlainText] = useState(false);
  const [encryptionType, setEncryptionType] = useState(t("SELECT"));
  const [encryptionParam, setEncryptionParam] = useState("");
  const [encryptionDropdownItems, setEncryptionDropdownItems] = useState([]);
  
  // PGP specific state variables
  const [recipientUserId, setRecipientUserId] = useState("");
  const [additionalParameters, setAdditionalParameters] = useState("");
  const [fileNameWithin, setFileNameWithin] = useState("");
  const [pgpModalState, setPgpModalState] = useState({
    open: false,
    field: "",
    isFirstTime: true
  });
  const [currentPgpField, setCurrentPgpField] = useState("");

  const encryptionOptions = encryptionDropdownItems.map((e, i) => {
    return (
      <MenuItem value={e.value} key={e.value}>
        {e.type}
      </MenuItem>
    );
  });

  const handleEncryptionChange = (checked) => {
    setEncryption(checked);
    if (!checked) {
      setRemovePlainText(false);
      setEncryptionType("NONE");
      setEncryptionParam("");

      const modifyProps = {
        DONT_RETAIN_PLAIN_TEXT: null,
        ENCRYPTION_TYPE: null,
        ENCRYPTION_PARAM1: null,
      };
      debounceUpdateAndSave({ remove: modifyProps });
    }
  };

  const handleRemovePlainTextChange = (value, setState, key) => {
    setState(value);
    debounceUpdateAndSave({ [key]: value === true ? "TRUE" : "FALSE" });
  };

  const handleEncryptionTypeChange = (value, setState, key) => {
    setState(value);
    if(value === 'NONE') {
       debounceUpdateAndSave({add: {[key]: value, ENCRYPTION_PARAM1: null}})
    } else if(value !== 'PGP' && screen === 'SEND_DATA') {
      debounceUpdateAndSave({add: {[key]: value, ENCRYPTION_PARAM1: null, ENCRYPTION_PARAM_PGP: null, FILENAME_WITHIN_ENC: null}})
    } else {
      value === "PGP" && setRecipientUserId("");
      debounceUpdateAndSave({add : { [key]: value, ...(value === "PGP" ? { ENCRYPTION_PARAM1: null} : {})}})
    }

  }
  const handleEncryptionParamChange = (value, setState, key) => {
    setState(value);
    value ? debounceUpdateAndSave({[key]: encryptString(value, process.env.REACT_APP_SECRET_KEY2)}) : debounceUpdateAndSave({ [key]: value });
    
  };

  // PGP field handlers
  const handlePgpFieldChange = (value, setState, key) => {
    setState(value);
    value ? debounceUpdateAndSave({[key]: encryptString(value, process.env.REACT_APP_SECRET_KEY2)}) : debounceUpdateAndSave({ [key]: value });
  };

  const handlePgpModalOpen = (fieldType) => {
    setCurrentPgpField(fieldType);
    setPgpModalState({
      open: true,
      field: fieldType,
      isFirstTime: true
    });
  };

  const handlePgpModalClose = () => {
    setPgpModalState({
      open: false,
      field: "",
      isFirstTime: true
    });
  };

  const handlePgpModalValueChange = (value) => {
    switch (currentPgpField) {
      case 'additionalParameters':
        setAdditionalParameters(value);
        debounceUpdateAndSave({ "ENCRYPTION_PARAM_PGP": value });
        break;
      case 'fileNameWithin':
        setFileNameWithin(value);
        debounceUpdateAndSave({ "FILENAME_WITHIN_ENC": value });
        break;
      default:
        break;
    }
    setCurrentPgpField("");
    handlePgpModalClose();
  };

  const debounceUpdateAndSave = debounce(updateAndSaveData, 500);

  const getEncryptionList = async (account) => {
    if (!account) return;
    const response = await dispatch(
      api.endpoints.getBridgegateConfig.initiate({ account })
    );

    if (response?.data?.current_state === "SUCCESS") {
      const list = Array.isArray(
        response?.data?.data?.ENCRYPTION_LIST?.ENCRYPTION
      )
        ? response?.data?.data?.ENCRYPTION_LIST?.ENCRYPTION
        : [response?.data?.data?.ENCRYPTION_LIST?.ENCRYPTION];

      const formattedList = list.map((item) => ({
        value: item?.TYPE,
        type: item?.TYPE,
      }));

      // Add the default 'NONE' option at the start
      formattedList.unshift({ value: "NONE", type: t("SELECT") });
      setEncryptionDropdownItems(formattedList);
    }
  };

  useEffect(() => {
    setEncryption(
      // (connectionDetails?.DONT_RETAIN_PLAIN_TEXT === "TRUE" ||
        // connectionDetails?.DONT_RETAIN_PLAIN_TEXT === true) &&
        (connectionDetails?.hasOwnProperty("ENCRYPTION_TYPE") &&
          connectionDetails?.ENCRYPTION_TYPE !== "NONE")
    );
    setRemovePlainText(
      connectionDetails?.DONT_RETAIN_PLAIN_TEXT === "TRUE" ||
        connectionDetails?.DONT_RETAIN_PLAIN_TEXT === true
    );
    setEncryptionType(connectionDetails?.ENCRYPTION_TYPE || "NONE");
    setEncryptionParam(connectionDetails?.ENCRYPTION_PARAM1 ? (decryptString(connectionDetails?.ENCRYPTION_PARAM1, process.env.REACT_APP_SECRET_KEY2)) : '');
    
    // PGP specific fields initialization
    setRecipientUserId(connectionDetails?.ENCRYPTION_PARAM1 ? (decryptString(connectionDetails?.ENCRYPTION_PARAM1, process.env.REACT_APP_SECRET_KEY2)) : '');
    setAdditionalParameters(connectionDetails?.ENCRYPTION_PARAM_PGP || '');
    setFileNameWithin(connectionDetails?.FILENAME_WITHIN_ENC || '');
  }, [connectionDetails]);

  useEffect(() => {
    if (encryption) getEncryptionList(account);
  }, [encryption]);

  useEffect(() => {
    if (disableEncrypt) {
      // Clear all encryption-related values when encryption is disabled
      const clearProps = {
        DONT_RETAIN_PLAIN_TEXT: null,
        ENCRYPTION_TYPE: null,
        ENCRYPTION_PARAM1: null,
        ENCRYPTION_PARAM_PGP: null,
        FILENAME_WITHIN_ENC: null,
      };
      debounceUpdateAndSave({ remove: clearProps });
    }
  }, [disableEncrypt]);

  return (
    <div>
      <Accordion sx={styles.accordianContainer} defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="accordian-override"
          sx={styles.accordianHeader}>
          <SectionHeader text={screen === "GET_DATA" ? t("LBL_DECRYPTION") : t("LBL_ENCRYPTION")} />
        </AccordionSummary>
        <AccordionDetails sx={styles.accordianContainer}>
          {/* Encryption/Decryption checkbox */}
          <FormControlLabel
            disabled={disableEncrypt || selectedNode?.OUTBOUND?.AS2_COMPRESSION === "TRUE" || selectedNode?.INBOUND?.AS2_COMPRESSION === "TRUE" || selectedNode?.OUTBOUND?.hasOwnProperty("ZIP") || selectedNode?.INBOUND?.hasOwnProperty("ZIP")}
            label={
              screen === "GET_DATA" ? t("LBL_DECRYPTION") : t("LBL_ENCRYPTION")
            }
            sx={{ height: "30px" }}
            className="width-fit-content"
            control={
              <Checkbox
                checked={encryption}
                size="small"
                name="encrypt"
                onChange={(e, checked) => handleEncryptionChange(checked)}
              />
            }
          />
          {encryption && !disableEncrypt && (
            <>
              {/*Remove Archive of decrypted file checkbox  */}
              <FormControlLabel
                label={t("LBL_ENCRYPTION_REM_PLAINTEXT")}
                sx={{ height: "30px" }}
                className="width-fit-content"
                control={
                  <Checkbox
                    checked={removePlainText}
                    size="small"
                    name="encrypt"
                    onChange={(e, checked) =>
                      handleRemovePlainTextChange(
                        checked,
                        setRemovePlainText,
                        "DONT_RETAIN_PLAIN_TEXT"
                      )
                    }
                  />
                }
              />
              {/* Decryption Type/ Encryption Type Dropdown */}
              <FormControl className="width-100 margin-top-4">
                <InputLabel id="encryption-dropdown">
                  {screen === "GET_DATA"
                    ? t("LBL_DECRYPTION_TYPE")
                    : t("LBL_ENCRYPTION_TYPE")}
                </InputLabel>
                <Select
                  size="small"
                  value={encryptionType}
                  label={
                    screen === "GET_DATA"
                      ? t("LBL_DECRYPTION_TYPE")
                      : t("LBL_ENCRYPTION_TYPE")
                  }
                  onChange={(e) =>
                    handleEncryptionTypeChange(
                      e.target.value,
                      setEncryptionType,
                      "ENCRYPTION_TYPE"
                    )
                  }>
                  {encryptionOptions}
                </Select>
              </FormControl>
              {/* Crypto Key Input */}
              {encryptionType !== "NONE" && 
               encryptionDropdownItems.some(item => item.value === encryptionType) && (
                (encryptionType === 'PGP' || encryptionType === 'GPG') && screen === 'SEND_DATA' ? (
                <>
                  {/* Recipient User ID */}
                  <FormControl className="width-100 margin-top-16">
                    <TextField
                      className="width-100"
                      size="small"
                      variant="outlined"
                      label={t("LBL_RECIPIENT_USER_ID")}
                      value={recipientUserId}
                      onChange={(e) =>
                        handlePgpFieldChange(
                          e.target.value,
                          setRecipientUserId,
                          "ENCRYPTION_PARAM1"
                        )
                      }
                    />
                  </FormControl>

                  {/* Additional Parameters */}
                  <FormControl className="width-100 margin-top-16">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        className="width-100"
                        size="small"
                        variant="outlined"
                        multiline
                        maxRows={3}
                        label={t("LBL_PGP_PARAMETERS")}
                        value={additionalParameters}
                        onChange={(e) =>
                          handlePgpFieldChange(
                            e.target.value,
                            setAdditionalParameters,
                            "ENCRYPTION_PARAM_PGP"
                          )
                        }
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handlePgpModalOpen('additionalParameters')}
                        sx={{ width: '40px', minWidth: '40px' }}
                      >
                        <MoreHorizOutlined fontSize="small" />
                      </Button>
                    </Box>
                  </FormControl>

                  {/* File Name Within */}
                  <FormControl className="width-100 margin-top-16">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        className="width-100"
                        size="small"
                        variant="outlined"
                        label={t("FILENAME_WITHIN_ENC")}
                        value={fileNameWithin}
                        onChange={(e) =>
                          handlePgpFieldChange(
                            e.target.value,
                            setFileNameWithin,
                            "FILENAME_WITHIN_ENC"
                          )
                        }
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handlePgpModalOpen('fileNameWithin')}
                        sx={{ width: '40px', minWidth: '40px' }}
                      >
                        <MoreHorizOutlined fontSize="small" />
                      </Button>
                    </Box>
                  </FormControl>
                </>) : 
                <FormControl className="width-100 margin-top-16">
                  <SmartPasswordField
                    className="width-100"
                    size="small"
                    type="password"
                    variant="outlined"
                    autoComplete="off"
                    label={
                      encryptionType === "JCE"
                        ? t("LBL_CRYPTO_KEY")
                        : t("LBL_SECRET_PASSPHRASE")
                    }
                    value={encryptionParam}
                    onChange={(e) =>
                      handleEncryptionParamChange(
                        e.target.value,
                        setEncryptionParam,
                        "ENCRYPTION_PARAM1"
                      )
                    }
                  />
                </FormControl>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* WorkflowSessionModal for PGP fields */}
      <WorkflowSessionModal
        openModal={pgpModalState}
        setModalOpen={setPgpModalState}
        handleClose={handlePgpModalClose}
        initialValue={currentPgpField === 'additionalParameters' ? additionalParameters : fileNameWithin}
        valuesHandler={handlePgpModalValueChange}
        showWorkflowSession={true}
        showAccountData={true}
        showDateAndTime={true}
        showSystemData={true}
        showCallingWorkflow={true}
        valueLabel={currentPgpField === 'additionalParameters' ? 
          (t("LBL_PGP_PARAMETERS")) : 
          (t("FILENAME_WITHIN_ENC"))
        }
        inputType="text"
      />
    </div>
  );
}

export default WorkflowEncryptDecrypt;
const styles = {
  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset",
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset",
  },
  accordianContent: {
    border: "none",
    padding: "0 15px",
    margin: "0",
    backgroundColor: "unset",
  },
};
